import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { parseISO } from "date-fns";
import { useBaseMutation } from "src/api/core/useBaseQuery";
import { NursePatientListResponse } from "./models";
import { isPatientUnder18, isPatientNewborn } from "../../utils";

import { useAppDispatch, useAppSelector } from "src/app/hooks";
import { toLocalDate } from "src/utils/date/dateHelper";
import { getPainIcon } from "src/utils/painIconHelper";
import ByzMuiDataGrid from "src/components/core-components/mui/data-grid";
import { Box, Stack, Tab, Tabs, Tooltip, Divider, Checkbox, FormControlLabel, Backdrop, CircularProgress, Typography } from "@mui/material";
import { ByzMuiDatePicker } from "src/components/core-components/mui/inputs/ByzMuiDatePicker";
import { MenuModal } from "src/components/shared-components/modal/menuModal";
import ByzMuiTextField from "src/components/core-components/mui/inputs/ByzMuiTextField";
import { CustomModal } from "src/components/shared-components/modal/customModal";
import AddStockModal from "src/modules/nurse/stock/add-stock";
import DepartmentList from "src/components/shared-components/department-list";
import { GridColDef, GridRowId } from "@mui/x-data-grid-premium";
import StarBorderIcon from "@mui/icons-material/StarBorder";
import DetailPanel from "./detail-grid/index";
import { AddMedicalCardModal } from "src/components/shared-components/modal/add-medcard/AddMedicalCardModal";
import { OpenedMedcards } from "src/modules/medcard/opened-medcars";
import { useTranslate } from "src/locales";
import FloorList from "src/components/shared-components/floor-list";
import { ByzMuiIconButton } from "src/components/core-components/mui/buttons/ByzMuiIconButton";
import RequestServiceModal from "./service-process/request-service/index";
import AddServiceModal from "./service-process/add-service/index";
import TreatmentApplicationsModal from "src/modules/nurse/order-medicine";
import ByzDataGridToolbar from "src/components/core-components/mui/data-grid/toolbar";
import ByzMuiSearchButton from "src/components/core-components/mui/buttons/ByzMuiSearchButton";
import { axiosInstance } from "src/api/config/axiosInstance";
import ByzOfficesList from "src/components/shared-components/ByzOfficesList";
import ByzNurseList from "src/components/shared-components/ByzNurseList";

import PatientProcess from "src/components/shared-components/modal/patientProcess";
import {
  HeightInfoModal,
  ProcessDirectionModal,
  AskDoctorModal,
  PregnantControlModal,
  HIVModal,
  PacemakerModal,
  ConsultationModal,
  LabRequestModal,
  PathologyModal,
  PatientComingFileChangeModal
} from "src/components/Add-Service-Modals";

import { ReactComponent as AddMaster } from "src/assets/icons/pages/nurse/add-master.svg";
import ByzStatusBadge from "src/components/shared-components/ByzStatusBadge";
import Iconify from "src/components/iconify";
import PatientCardView from "./card-view/PatientCardView";
import { ByzMuiSpeakToTextModal } from "src/components/shared-components/modal/ByzMuiSpeakToTextModal";
import ByzViewModeSelector from "src/components/shared-components/ByzViewModeSelector";
import { useBaseQuery } from "src/api/core/useBaseQuery";
import { setSelectedOfficeId as setSelectedOfficeIdGlobal } from "src/modules/auth/authenticationSlice";
import { useSelectedOffice } from "src/hooks/useSelectedOffice";
import {
  PatientAiAnalysisDrawer,
  PatientAiAnalysisReport,
  PatientAiAnalysisStatus,
} from "./card-view/ai-analysis";
import {
  buildNurseListPatientAnalyzeRequest,
  patientAnalyzeStream,
} from "src/api/ai-assistant/aiAssistantService";

function List(props: any) {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { user, selectedOfficeId: reduxOfficeId } = useAppSelector((state) => state.auth);
  const { effectiveOffId } = useSelectedOffice();

  const [selectedTab, setSelectedTab] = useState(0);
  const [selectedOfficeId, setSelectedOfficeId] = useState<number>(reduxOfficeId || 0);
  const [selectedDepartmentId, setSelectedDepartmentId] = useState<number>(0);
  const [selectedFloorId, setSelectedFloorId] = useState<number>(0);
  const [selectedNurseId, setSelectedNurseId] = useState<number>(0);
  const [viewMode, setViewMode] = useState<'list' | 'card'>('card');
  const [syncScroll, setSyncScroll] = useState(false);

  // Redux officeId değiştiğinde local state'i güncelle (e.g. login sonrası)
  useEffect(() => {
    if (reduxOfficeId && selectedOfficeId === 0) {
      setSelectedOfficeId(reduxOfficeId);
    }
  }, [reduxOfficeId, selectedOfficeId]);
  const [startDate, setStartDate] = useState<string>(() => toLocalDate(new Date()));
  const [endDate, setEndDate] = useState<string>(() => {
    const d = new Date();
    d.setDate(d.getDate() + 1);
    return toLocalDate(d);
  });
  const [searchTerm, setSearchTerm] = useState("");
  const [departmentName, setDepartmentName] = useState("");
  const [roomNumber, setRoomNumber] = useState("");
  const [age, setAge] = useState("");
  const [gender, setGender] = useState("");
  const [expandedRowIds, setExpandedRowIds] = useState<GridRowId[]>([]);

  const [speakToTextState, setSpeakToTextState] = useState<{
    open: boolean;
    row: any | null;
    mode: "followup" | "note";
  }>({ open: false, row: null, mode: "followup" });

  const { t } = useTranslate();
  const [visitTypeUpdating, setVisitTypeUpdating] = useState(false);
  const [visitTypeOverrides, setVisitTypeOverrides] = useState<Record<number, { id: number; name: string }>>({});
  const [aiAnalysisState, setAiAnalysisState] = useState<{
    open: boolean;
    patient: any | null;
    status: PatientAiAnalysisStatus;
    report: PatientAiAnalysisReport | null;
    stepFlushPending?: boolean;
  }>({
    open: false,
    patient: null,
    status: "idle",
    report: null,
    stepFlushPending: false,
  });

  const { data: visitTypesData } = useBaseQuery<any>({
    endpoint: "/nurse/api/Nurse/GetVisitTypes",
    queryKey: ["nurse-visit-types"],
    options: {
      staleTime: 5 * 60 * 1000,
      gcTime: 30 * 60 * 1000,
      refetchOnWindowFocus: false,
    },
  });

  const visitTypes = useMemo(() => {
    const list = (visitTypesData as any)?.data;
    return Array.isArray(list) ? list : [];
  }, [visitTypesData]);

  const nurserMutation = useBaseMutation<NursePatientListResponse>({
    endpoint: "/nurse/api/Nurse/GetNurseProcessList",
    method: "POST",
    data: {
      offId: effectiveOffId,
      userId: user?.id,
      hfId: selectedFloorId,
      deptId: selectedDepartmentId,
      startTime: startDate,
      endTime: endDate,
      isDischarge: selectedTab.toString(),
      nurseId: selectedNurseId,
    },
  });

  const {
    data: nurseData,
    // error: nurseError,
    mutate: getNursePatientList,
  } = nurserMutation;

  // İzolasyon ikonlarını cache'le (1 gün)
  const ISOLATION_ICONS_CACHE_KEY = "IsolationTypeIconsCache";
  const ONE_DAY_MS = 24 * 60 * 60 * 1000;
  const [isolationTypeIconsData, setIsolationTypeIconsData] = useState<any[] | null>(null);

  useEffect(() => {
    const cached = localStorage.getItem(ISOLATION_ICONS_CACHE_KEY);
    if (cached) {
      try {
        const parsed = JSON.parse(cached);
        if (parsed?.ts && parsed?.data && Date.now() - parsed.ts < ONE_DAY_MS) {
          setIsolationTypeIconsData(parsed.data);
          return;
        }
      } catch (err) {
        console.warn("Isolation icon cache parse error:", err);
      }
    }

    const fetchIcons = async () => {
      try {
        const res = await axiosInstance.get("isolation/api/IsolationType/GetIsolationTypeIcons");
        const data = res?.data?.data ?? res?.data ?? [];
        setIsolationTypeIconsData(data);
        localStorage.setItem(
          ISOLATION_ICONS_CACHE_KEY,
          JSON.stringify({ ts: Date.now(), data })
        );
      } catch (err) {
        console.error("Isolation icon fetch error:", err);
      }
    };

    fetchIcons();
  }, [ONE_DAY_MS]);

  const isolationIconsMap = useMemo(() => {
    const arr: any[] = Array.isArray(isolationTypeIconsData) ? isolationTypeIconsData : [];
    const map: Record<number, string> = {};
    arr.forEach((icon: any) => {
      const id = Number(icon?.id ?? icon?.ID);
      const svg = icon?.pics_svg ?? icon?.PICS_SVG;
      if (id && svg) map[id] = svg;
    });
    return map;
  }, [isolationTypeIconsData]);

  const getNursePatientListStable = useCallback(() => {
    getNursePatientList();
  }, [getNursePatientList]);

  const handleVisitTypeSaved = useCallback((patientId: number, visitType: { id: number; name: string } | null) => {
    if (!patientId || !visitType) {
      return;
    }

    setVisitTypeOverrides((prev) => ({
      ...prev,
      [patientId]: visitType,
    }));
  }, []);

  // Console'da görünen null hatalarını suppress et
  useEffect(() => {
    const originalError = console.error;
    const originalWarn = console.warn;

    // Suppress console.error
    console.error = (...args: any[]) => {
      const errorMsg = args[0]?.toString() || "";
      const stackMsg = args.join(" ") || "";
      if (
        (errorMsg.includes("Cannot read properties of null") && errorMsg.includes("reading 'row'")) ||
        (stackMsg.includes("Cannot read properties of null") && stackMsg.includes("reading 'row'")) ||
        (errorMsg.includes("TypeError") && errorMsg.includes("null") && errorMsg.includes("row"))
      ) {
        return; // Suppress null row errors
      }
      originalError.apply(console, args);
    };

    // Suppress console.warn for similar errors
    console.warn = (...args: any[]) => {
      const warnMsg = args[0]?.toString() || "";
      const stackMsg = args.join(" ") || "";
      if (
        (warnMsg.includes("Cannot read properties of null") && warnMsg.includes("reading 'row'")) ||
        (stackMsg.includes("Cannot read properties of null") && stackMsg.includes("reading 'row'"))
      ) {
        return; // Suppress null row warnings
      }
      originalWarn.apply(console, args);
    };

    // Global error handler
    const handleError = (event: ErrorEvent) => {
      const errorMsg = event.message || "";
      if (errorMsg.includes("Cannot read properties of null") && errorMsg.includes("reading 'row'")) {
        event.preventDefault();
        event.stopPropagation();
        return false;
      }
    };

    window.addEventListener("error", handleError);

    return () => {
      console.error = originalError;
      console.warn = originalWarn;
      window.removeEventListener("error", handleError);
    };
  }, []);

  useEffect(() => {
    // TABURCU tabına (1) geçildiğinde tarih aralığını son 1 güne ayarla
    if (selectedTab === 1) {
      const end = new Date();
      const start = new Date();
      end.setDate(end.getDate() + 1);
      setStartDate(toLocalDate(start));
      setEndDate(toLocalDate(end));
    }

    getNursePatientListStable();
  }, [selectedTab, getNursePatientListStable]);

  // Şube değiştiğinde listeyi yenile
  useEffect(() => {
    if (effectiveOffId > 0) {
      getNursePatientListStable();
    }
  }, [effectiveOffId, getNursePatientListStable]);

  const openModalMC = (row: any) => {
    handleOpenMC();
    setRowID(row.id);
    setUpn(row.upn ? String(row.upn) : "");
    setTitle(row.name + " " + row.surname);
    setDescription(row.deptname);
    setAge(row.age);
    setGender(row.sexuality);
  };
  const openModalMedcard = (row: any) => {
    handleOpenMedCard();
    setRowID(row.id);
    setUpn(row.upn ? String(row.upn) : "");
    setTitle(row.name + " " + row.surname);
    setDescription(row.deptname + " " + row.bedinfo);
    setAge(row.age);
    setGender(row.sexuality);
  };

  const openRequestServiceModal = (row: any) => {
    handleOpenRequestService();
    setRowID(row.id);
    setUpn(row.upn ? String(row.upn) : "");
    setTitle(row.name + " " + row.surname);
    setDescription(row.deptname + " " + row.bedinfo);
    setAge(row.age);
    setGender(row.sexuality);
  };

  const openAddServiceModal = (row: any) => {
    handleOpenAddService();
    setRowID(row.id);
    setUpn(row.upn ? String(row.upn) : "");
    setTitle(row.name + " " + row.surname);
    setDescription(row.deptname + " " + row.bedinfo);
    setAge(row.age);
    setGender(row.sexuality);
  };

  const openAddStockModal = (row: any) => {
    setOpenAddStock(true);
    setRowID(row.id);
    setUpn(row.upn ? String(row.upn) : "");
    setTitle(row.name + " " + row.surname);
    setDescription(row.deptname + " " + row.bedinfo);
    setAge(row.age);
    setGender(row.sexuality);
  };

  const openProcessesModal = (row: any) => {
    navigate(`/nurse/patient-process/${row.id}`);
  };

  const openTreatmentApplicationsModal = (row: any) => {
    setTreatmentApplicationsPatient(row);
    setOpenTreatmentApplicationsModalState(true);
  };

  const openModal = (row: any) => {
    handleOpen();

    const ageUnder18 = isPatientUnder18(row?.age);
    const ageNewborn = isPatientNewborn(row?.age);


    const modalMenus = [
      {
        name: t("menus.add-service"),
        icon: "mdi-light:plus",
        url: "",
        image: "/assets/icons/pages/nurse/add-processes/service-add.svg",
        clickFunction: () => {
          openAddServiceModal(row);
        },
      },
      {
        name: t("menus.add-stock"),
        icon: "mdi-light:plus",
        url: "",
        image: "/assets/icons/pages/nurse/add-process.svg",
        clickFunction: () => {
          openAddStockModal(row);
        },
      },
      {
        name: t("menus.request-service"),
        icon: "mdi-light:plus",
        url: "",
        image: "/assets/icons/pages/nurse/add-processes/service-request.svg",
        clickFunction: () => {
          openRequestServiceModal(row);
        },
      },
      {
        name: t("menus.add-medcard"),
        icon: "mdi-light:AddBox",
        url: "",
        image: "/assets/icons/pages/nurse/add-processes/process-add.svg",
        clickFunction: () => {
          openModalMC(row);
        },
      },
      {
        name: t("menus.request-isolation"),
        icon: "mdi-light:lightbulb",
        url: "/isolation/request/" + row.id,
        image: "/assets/icons/pages/nurse/add-processes/isolation-request.svg",
        clickFunction: () => {
          openModalMC(row);
        },
      },
      {
        name: t("menus.request-special-order"),
        icon: "mdi-light:order",
        url: "/order/" + row.id,
        image: "/assets/icons/pages/nurse/add-processes/order.svg",
        disabled: true,
        tooltip: "under-development",
      },
      {
        name: t("menus.surgery"),
        icon: "mdi-light:surgery",
        url: "/patient/nurse-application-screen/" + row.id,
        image: "/assets/icons/pages/nurse/add-processes/surgery.svg",
      },
      {
        name: t("menus.examination"),
        icon: "mdi-light:examination",
        url: "/nurse/examination/" + row.id,
        image: "/assets/icons/pages/nurse/add-processes/treatment-application.svg",
      },
      {
        name: t("menus.opened-medcards"),
        icon: "mdi-light:score",
        url: "",
        image: "/assets/icons/pages/nurse/add-processes/opened-cards.svg",
        clickFunction: () => {
          openModalMedcard(row);
        },
      },
      {
        name: t("menus.patient-care-plan"),
        icon: "mdi-light:patient-care-plan",
        url: "/patient/patient-care-add/" + row.id,
        image: "/assets/icons/pages/nurse/add-processes/care-plans.svg",
      },
      {
        name: t("menus.diet"),
        url: "/patient/diet-procedures/" + row.id,
        image: "/assets/icons/pages/nurse/add-processes/diet.svg",
      },
      {
        name: t("menus.nurse"),
        url: "/nurse/nurse-form/" + row.id,
        image: "/assets/icons/pages/nurse/add-processes/nurse.svg",
        disabled: true,
        tooltip: "under-development",
      },
      {
        name: "Tedavi Uygulama",
        url: "",
        image: "/assets/icons/pages/nurse/add-processes/treatment-application.svg",
        clickFunction: () => {
          openTreatmentApplicationsModal(row);
        },
      },
      {
        name: t("menus.newborn-followup"),
        url: "/medcard/newborn-trace/" + row.id,
        image: "/assets/icons/pages/nurse/add-processes/newborn-trace.svg",
        hidden: !ageNewborn,
      },

      {
        name: t("menus.pediatrics"),
        url: "/medcard/pediatric-assessment/" + row.id,
        image: "/assets/icons/pages/nurse/add-processes/pediatri.svg",
        hidden: !ageUnder18,
      },

      {
        name: t("menus.vitals"),
        url: "/medcard/vital-signs/" + row.id,
        image: "/assets/icons/pages/nurse/add-processes/vital-signs.svg",
      },
      {
        name: "Hasta Tabelası",
        url: "/nurse/patient-table/" + row.id,
        image: "/assets/icons/nurse/patient-table.svg",
      },
      {
        name: t("menus.patient-monitoring"),
        url: "/medcard/patient-monitoring/" + row.id,
        image: "/assets/icons/pages/nurse/add-processes/patient-monitoring.svg",
      },
      {
        name: t("menus.anthropometry"),
        url: "/medcard/height-weight/" + row.id,
        image: "/assets/icons/pages/nurse/add-processes/height-weight.svg",
      },
    ];

    setmenus(modalMenus.filter(m => !m.hidden));

    setRowID(row.id);
    setUpn(row.upn);
    setTitle(row.name + " " + row.surname);
    setDescription(row.deptname);
    setDepartmentName(row.deptname);
    setRoomNumber(row.bedinfo);
    setAge(row.age);
    setGender(row.sexuality);
  };




  const columns: GridColDef[] = [
    {
      field: "Ekle",
      headerName: "",
      width: 80,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderHeader: () => (
        <ByzMuiIconButton
          onClick={handleOpenColumnSelector}
        >
          <StarBorderIcon />
        </ByzMuiIconButton>
      ),
      renderCell: (params) => {
        if (!params?.row) return null;
        return (
          <ByzMuiIconButton
            onClick={(event: React.MouseEvent) => {
              // Satıra tıklayınca detay panel açılmasını engelle
              event.stopPropagation();
              openModal(params.row);
            }}
          >
            <AddMaster />
          </ByzMuiIconButton>
        );
      },
    },
    // Gereken kolonlar (Dosya No, Prot No, Ad, Soyad, Oda No, Cinsiyet, İzolasyon, Ameliyat, Doktoru, Diğer Bilgiler)
    {
      field: "upn",
      headerName: t("file-number"),
      width: 100,
      valueGetter: (paramsOrValue: any, rowOrUndefined?: any) => {
        const row = paramsOrValue?.row ?? rowOrUndefined;
        return row?.upn ?? "-";
      }
    },
    {
      field: "protid",
      headerName: t("protocol-number"),
      width: 100,
      valueGetter: (paramsOrValue: any, rowOrUndefined?: any) => {
        const row = paramsOrValue?.row ?? rowOrUndefined;
        return row?.protid ?? "-";
      }
    },
    {
      field: "name",
      headerName: t("name"),
      flex: 1,
      minWidth: 120,
      renderCell: (params) => (
        <Box sx={{ fontWeight: 700 }}>{params.row?.name ?? "-"}</Box>
      ),
    },
    {
      field: "surname",
      headerName: t("surname"),
      flex: 1,
      minWidth: 120,
      renderCell: (params) => (
        <Box sx={{ fontWeight: 700 }}>{params.row?.surname ?? "-"}</Box>
      ),
    },
    {
      field: "age",
      headerName: t("nurse.patient-list.columns.age"),
      width: 120,
      hideable: true,
      valueGetter: (paramsOrValue: any, rowOrUndefined?: any) => {
        const row = paramsOrValue?.row ?? rowOrUndefined;
        return row?.age ?? "-";
      }
    },
    {
      field: "bedinfo",
      headerName: t("room-number"),
      width: 80,
      valueGetter: (paramsOrValue: any, rowOrUndefined?: any) => {
        const row = paramsOrValue?.row ?? rowOrUndefined;
        return row?.bedinfo ?? "-";
      }
    },
    {
      field: "sexuality",
      headerName: t("gender"),
      width: 110,
      renderCell: (params) => {
        if (!params?.row) return null;
        return <ByzStatusBadge type="gender" value={params.row?.sexuality} />;
      },
    },
    {
      field: "isolation",
      headerName: t("nurse.patient-list.columns.isolation"),
      width: 120,
      renderCell: (params) => {
        if (!params?.row) return null;
        const ids = Array.isArray(params.row?.isolationtypeids)
          ? params.row.isolationtypeids
          : [];
        if (!ids.length) return null;

        return (
          <Box sx={{ display: "flex", alignItems: "center", width: "100%" }}>
            {ids.map((id: any, index: number) => {
              const svg = isolationIconsMap[Number(id)];
              if (!svg) return null;
              return (
                <Box
                  key={`iso-${id}`}
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: "24px",
                    border: "1px solid #FFF",
                    background:
                      "linear-gradient(0deg, rgba(195, 202, 217, 0.55) 0%, rgba(195, 202, 217, 0.55) 100%), #FFF",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    ml: index === 0 ? 0 : "-10px",
                    zIndex: ids.length - index,
                  }}
                >
                  <img
                    src={`data:image/svg+xml;base64,${svg}`}
                    alt=""
                    style={{ width: 20, height: 20 }}
                  />
                </Box>
              );
            })}
          </Box>
        );
      },
    },
    {
      field: "operationstatus",
      headerName: t("nurse.patient-list.columns.operation-status"),
      flex: 1,
      minWidth: 160,
      renderCell: (params) => {
        if (!params?.row) return null;
        return (
          <ByzStatusBadge
            type="surgery"
            value={params.row?.operationstatus}
            customColor={params.row?.operationstatuscolor}
          />
        );
      },
    },
    {
      field: "drname",
      headerName: t("nurse.patient-list.columns.doctor"),
      flex: 1,
      minWidth: 150,
      valueGetter: (paramsOrValue: any, rowOrUndefined?: any) => {
        const row = paramsOrValue?.row ?? rowOrUndefined;
        return row?.drname ?? "-";
      }
    },
    {
      field: "other_info",
      headerName: t("nurse.patient-list.columns.other-info"),
      width: 430,
      sortable: false,
      filterable: false,
      renderCell: (params) => {
        if (!params?.row) return null;
        const row = params.row;

        const iconConfig: any[] = [
          { key: "hasallergy", label: t("nurse-icons.allergy"), imageSrc: "/assets/icons/pages/nurse/alergy.svg" },
          { key: "hasorder", label: t("nurse-icons.order"), imageSrc: "/assets/icons/pages/nurse/order-pill-green.svg" },
          { key: "hasairway", label: t("nurse-icons.airway"), imageSrc: "/assets/icons/pages/nurse/air-way.svg" },
          { key: "hasradiology", label: t("nurse-icons.radiology"), imageSrc: "/assets/icons/pages/nurse/radiology.svg" },
          { key: "hasbloodrequest", label: t("nurse-icons.blood"), imageSrc: "/assets/icons/pages/nurse/blood-request.svg" },
          { key: "consultation", label: t("nurse-icons.consultation"), imageSrc: "/assets/icons/pages/nurse/consultation.svg" },
          { key: "hasepikriz", label: t("nurse-icons.epicrisis"), imageSrc: "/assets/icons/pages/nurse/epicrisis.svg" },
          { key: "haslab", label: t("nurse-icons.lab"), imageSrc: "/assets/icons/pages/nurse/laboratuar.svg" },
          { key: "haspain", label: t("nurse-icons.pain"), imageSrc: "/assets/icons/pages/nurse/painList/pain-0.svg" },
          { key: "hasfallrisk", label: t("nurse-icons.fall-risk"), imageSrc: "/assets/icons/pages/nurse/fall-risk.svg" },
          { key: "hasfallen", label: t("nurse-icons.fell"), imageSrc: "/assets/icons/pages/nurse/fallen.svg" },
          { key: "hascompanion", label: t("nurse-icons.companion"), imageSrc: "/assets/icons/pages/nurse/companion.svg" },
          { key: "haspregnant", label: t("nurse-icons.pregnant"), imageSrc: "/assets/icons/pages/nurse/pregnant.svg" },
        ];

        return (
          <Stack
            direction="row"
            spacing={0.5}
            sx={{
              width: "100%",
              height: "100%",
              px: 1,
              alignItems: "center",
              justifyContent: "flex-start"
            }}
          >
            {iconConfig.map((item) => {
              const val = row[item.key] ?? row[item.key.toLowerCase()];
              const isActive = item.key === 'haspain'
                ? (val != null && val > 0)
                : (val === 1 || val === true || val === "1" || val === "true" || (item.key === 'consultation' && val > 0));

              if (item.imageSrc) {
                const iconSrc = item.key === 'haspain'
                  ? getPainIcon(val, item.imageSrc)
                  : item.imageSrc;

                return (
                  <Tooltip key={item.key} title={item.label}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        p: 0.5,
                        opacity: 1,
                        filter: isActive
                          ? 'none'
                          : 'grayscale(100%)'
                      }}
                    >
                      <img
                        src={iconSrc}
                        alt={item.label}
                        style={{ width: 22, height: 22, maxWidth: 'none' }}
                      />
                    </Box>
                  </Tooltip>
                );
              }

              return (
                <Tooltip key={item.key} title={item.label}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', p: 0.5 }}>
                    <Iconify
                      icon={item.icon}
                      width={22}
                      sx={{
                        color: isActive ? "#2e7d32" : "#919eab",
                        opacity: 1
                      }}
                    />
                  </Box>
                </Tooltip>
              );
            })}
          </Stack>
        );
      },
    },

    // Ek kolonlar: Yıldız (Column Selector) içinden açılabilir
    {
      field: "diseasecodes",
      headerName: t("nurse.patient-list.columns.diagnosis"),
      flex: 1,
      hideable: true,
      valueGetter: (paramsOrValue: any, rowOrUndefined?: any) => {
        const row = paramsOrValue?.row ?? rowOrUndefined;
        return row?.diseasecodes ?? "-";
      }
    },
    {
      field: "deptname",
      headerName: t("nurse.patient-list.columns.department"),
      flex: 1,
      hideable: true,
      valueGetter: (paramsOrValue: any, rowOrUndefined?: any) => {
        const row = paramsOrValue?.row ?? rowOrUndefined;
        return row?.deptname ?? "-";
      }
    },
    {
      field: "assname",
      headerName: t("nurse.patient-list.columns.institution"),
      flex: 1,
      hideable: true,
      valueGetter: (paramsOrValue: any, rowOrUndefined?: any) => {
        const row = paramsOrValue?.row ?? rowOrUndefined;
        return row?.assname ?? "-";
      }
    },
    {
      field: "secondaryassname",
      headerName: t("nurse.patient-list.columns.secondary-institution"),
      flex: 1,
      hideable: true,
      valueGetter: (paramsOrValue: any, rowOrUndefined?: any) => {
        const row = paramsOrValue?.row ?? rowOrUndefined;
        return row?.secondaryassname ?? "-";
      }
    },
    {
      field: "floorinfo",
      headerName: t("nurse.patient-list.columns.floor"),
      flex: 1,
      hideable: true,
      valueGetter: (paramsOrValue: any, rowOrUndefined?: any) => {
        const row = paramsOrValue?.row ?? rowOrUndefined;
        return row?.floorinfo ?? "-";
      }
    },
    {
      field: "patienttype",
      headerName: t("nurse.patient-list.columns.patient-type"),
      flex: 1,
      hideable: true,
      valueGetter: (paramsOrValue: any, rowOrUndefined?: any) => {
        const row = paramsOrValue?.row ?? rowOrUndefined;
        return row?.patienttype ?? "-";
      }
    },
    {
      field: "patientlanguage",
      headerName: t("nurse.patient-list.columns.patient-language"),
      flex: 1,
      hideable: true,
      valueGetter: (paramsOrValue: any, rowOrUndefined?: any) => {
        const row = paramsOrValue?.row ?? rowOrUndefined;
        return row?.patientlanguage ?? "-";
      }
    },
    {
      field: "patientgroup",
      headerName: t("nurse.patient-list.columns.patient-group"),
      flex: 1,
      hideable: true,
      valueGetter: (paramsOrValue: any, rowOrUndefined?: any) => {
        const row = paramsOrValue?.row ?? rowOrUndefined;
        return row?.patientgroup ?? "-";
      }
    },
    {
      field: "hospitalizationduration",
      headerName: t("nurse.patient-list.columns.hospitalization-duration"),
      flex: 1,
      hideable: true,
      valueGetter: (paramsOrValue: any, rowOrUndefined?: any) => {
        const row = paramsOrValue?.row ?? rowOrUndefined;
        return row?.hospitalizationduration ?? "-";
      }
    },
    {
      field: "dischargetime",
      headerName: t("nurse.patient-list.columns.discharge-time"),
      flex: 1,
      hideable: true,
      valueGetter: (paramsOrValue: any, rowOrUndefined?: any) => {
        const row = paramsOrValue?.row ?? rowOrUndefined;
        return row?.dischargetime ?? "-";
      }
    },
    {
      field: "bloodgroup",
      headerName: t("nurse.patient-list.columns.blood-group"),
      flex: 1,
      hideable: true,
      valueGetter: (paramsOrValue: any, rowOrUndefined?: any) => {
        const row = paramsOrValue?.row ?? rowOrUndefined;
        return row?.bloodgroup ?? "-";
      }
    },
    {
      field: "hasfallen",
      headerName: t("nurse.patient-list.columns.fell"),
      flex: 1,
      hideable: true,
      valueGetter: (paramsOrValue: any, rowOrUndefined?: any) => {
        const row = paramsOrValue?.row ?? rowOrUndefined;
        return row?.hasfallen === 1 ? t("exists") : t("none");
      }
    },
    {
      field: "filestatus",
      headerName: t("nurse.patient-list.columns.file-status"),
      flex: 1,
      hideable: true,
      valueGetter: (paramsOrValue: any, rowOrUndefined?: any) => {
        const row = paramsOrValue?.row ?? rowOrUndefined;
        return row?.filestatus ?? "-";
      }
    },
    {
      field: "nurse",
      headerName: t("nurse.patient-list.columns.nurse"),
      flex: 1,
      hideable: true,
      valueGetter: (paramsOrValue: any, rowOrUndefined?: any) => {
        const row = paramsOrValue?.row ?? rowOrUndefined;
        return row?.nursename ?? "-";
      }
    },
    {
      field: "consultation",
      headerName: t("nurse.patient-list.columns.consultation"),
      flex: 1,
      hideable: true,
      valueGetter: (paramsOrValue: any, rowOrUndefined?: any) => {
        const row = paramsOrValue?.row ?? rowOrUndefined;
        return row?.consultation ?? 0;
      }
    },
    {
      field: "psychologist",
      headerName: t("nurse.patient-list.columns.psychologist"),
      flex: 1,
      hideable: true,
      valueGetter: (paramsOrValue: any, rowOrUndefined?: any) => {
        const row = paramsOrValue?.row ?? rowOrUndefined;
        return row?.psychologist ?? "-";
      }
    },
    {
      field: "treatmenttype",
      headerName: t("nurse.patient-list.columns.treatment-type"),
      flex: 1,
      hideable: true,
      valueGetter: (paramsOrValue: any, rowOrUndefined?: any) => {
        const row = paramsOrValue?.row ?? rowOrUndefined;
        return row?.treatmenttype ?? "-";
      }
    },
    {
      field: "iscalledfortest",
      headerName: t("nurse.patient-list.columns.is-called-for-test"),
      flex: 1,
      hideable: true,
      valueGetter: (paramsOrValue: any, rowOrUndefined?: any) => {
        const row = paramsOrValue?.row ?? rowOrUndefined;
        return row?.iscalledfortest === 1 ? t("yes") : t("no");
      }
    },
    {
      field: "hospitalizationtime",
      headerName: t("nurse.patient-list.columns.hospitalization-time"),
      flex: 1,
      hideable: true,
      valueGetter: (paramsOrValue: any, rowOrUndefined?: any) => {
        const row = paramsOrValue?.row ?? rowOrUndefined;
        return row?.hospitalizationtime ?? "-";
      }
    },
    {
      field: "observationstart",
      headerName: t("nurse.patient-list.columns.observation-start"),
      flex: 1,
      hideable: true,
      valueGetter: (paramsOrValue: any, rowOrUndefined?: any) => {
        const row = paramsOrValue?.row ?? rowOrUndefined;
        return row?.observationstart ?? "-";
      }
    },
    {
      field: "observationend",
      headerName: t("nurse.patient-list.columns.observation-end"),
      flex: 1,
      hideable: true,
      valueGetter: (paramsOrValue: any, rowOrUndefined?: any) => {
        const row = paramsOrValue?.row ?? rowOrUndefined;
        return row?.observationend ?? "-";
      }
    },
    {
      field: "ispregnant",
      headerName: t("nurse.patient-list.columns.pregnant-breastfeeding"),
      flex: 1,
      hideable: true,
      valueGetter: (paramsOrValue: any, rowOrUndefined?: any) => {
        const row = paramsOrValue?.row ?? rowOrUndefined;
        return row?.ispregnant === 1 ? t("yes") : t("no");
      }
    },
    {
      field: "hasration",
      headerName: t("nurse.patient-list.columns.diet"),
      flex: 1,
      hideable: true,
      valueGetter: (paramsOrValue: any, rowOrUndefined?: any) => {
        const row = paramsOrValue?.row ?? rowOrUndefined;
        return row?.hasration === 1 ? t("exists") : t("none");
      }
    },
    {
      field: "hascompanion",
      headerName: t("nurse.patient-list.columns.companion"),
      flex: 1,
      hideable: true,
      valueGetter: (paramsOrValue: any, rowOrUndefined?: any) => {
        const row = paramsOrValue?.row ?? rowOrUndefined;
        return row?.hascompanion === 1 ? t("exists") : t("none");
      }
    },
    {
      field: "hasprosthesis",
      headerName: t("nurse.patient-list.columns.prosthesis"),
      flex: 1,
      hideable: true,
      valueGetter: (paramsOrValue: any, rowOrUndefined?: any) => {
        const row = paramsOrValue?.row ?? rowOrUndefined;
        return row?.hasprosthesis === 1 ? t("exists") : t("none");
      }
    },
    {
      field: "hasallergy",
      headerName: t("nurse.patient-list.columns.allergy"),
      flex: 1,
      hideable: true,
      valueGetter: (paramsOrValue: any, rowOrUndefined?: any) => {
        const row = paramsOrValue?.row ?? rowOrUndefined;
        return row?.hasallergy === 1 ? t("exists") : t("none");
      }
    },
    {
      field: "hasorder",
      headerName: t("nurse.patient-list.columns.order"),
      flex: 1,
      hideable: true,
      valueGetter: (paramsOrValue: any, rowOrUndefined?: any) => {
        const row = paramsOrValue?.row ?? rowOrUndefined;
        return row?.hasorder === 1 ? t("exists") : t("none");
      }
    },
    {
      field: "hasairway",
      headerName: t("nurse.patient-list.columns.airway"),
      flex: 1,
      hideable: true,
      valueGetter: (paramsOrValue: any, rowOrUndefined?: any) => {
        const row = paramsOrValue?.row ?? rowOrUndefined;
        return row?.hasairway === 1 ? t("exists") : t("none");
      }
    },
    {
      field: "hasradiology",
      headerName: t("nurse.patient-list.columns.radiology"),
      flex: 1,
      hideable: true,
      valueGetter: (paramsOrValue: any, rowOrUndefined?: any) => {
        const row = paramsOrValue?.row ?? rowOrUndefined;
        return row?.hasradiology === 1 ? t("exists") : t("none");
      }
    },
    {
      field: "hasbloodrequest",
      headerName: t("nurse.patient-list.columns.blood"),
      flex: 1,
      hideable: true,
      valueGetter: (paramsOrValue: any, rowOrUndefined?: any) => {
        const row = paramsOrValue?.row ?? rowOrUndefined;
        return row?.hasbloodrequest === 1 ? t("exists") : t("none");
      }
    },
    {
      field: "hasepikriz",
      headerName: t("nurse.patient-list.columns.epicrisis"),
      flex: 1,
      hideable: true,
      valueGetter: (paramsOrValue: any, rowOrUndefined?: any) => {
        const row = paramsOrValue?.row ?? rowOrUndefined;
        return row?.hasepikriz === 1 ? t("exists") : t("none");
      }
    },
    {
      field: "hasmonitoring",
      headerName: t("nurse.patient-list.columns.patient-monitoring"),
      flex: 1,
      hideable: true,
      valueGetter: (paramsOrValue: any, rowOrUndefined?: any) => {
        const row = paramsOrValue?.row ?? rowOrUndefined;
        return row?.hasmonitoring === 1 ? t("exists") : t("none");
      }
    },
    {
      field: "haslab",
      headerName: t("nurse.patient-list.columns.lab"),
      flex: 1,
      hideable: true,
      valueGetter: (paramsOrValue: any, rowOrUndefined?: any) => {
        const row = paramsOrValue?.row ?? rowOrUndefined;
        return row?.haslab === 1 ? t("exists") : t("none");
      }
    },
    {
      field: "haspain",
      headerName: t("nurse.patient-list.columns.pain"),
      flex: 1,
      hideable: true,
      valueGetter: (paramsOrValue: any, rowOrUndefined?: any) => {
        const row = paramsOrValue?.row ?? rowOrUndefined;
        return row?.haspain === 1 ? t("exists") : t("none");
      }
    },
    {
      field: "hasfallrisk",
      headerName: t("nurse.patient-list.columns.fall-risk"),
      flex: 1,
      hideable: true,
      valueGetter: (paramsOrValue: any, rowOrUndefined?: any) => {
        const row = paramsOrValue?.row ?? rowOrUndefined;
        return row?.hasfallrisk === 1 ? t("exists") : t("none");
      }
    },


  ];

  const [openMenu, setOpenMenu] = useState(false);
  const [openMenuMC, setOpenMenuMC] = useState(false);
  const [openMedCards, setOpenMedCard] = useState(false);
  const [open, setOpen] = useState(false);
  const [openRequestService, setOpenRequestService] = useState(false);
  const [openAddService, setOpenAddService] = useState(false);
  const [openAddStock, setOpenAddStock] = useState(false);
  const [openHeightInfoModal, setOpenHeightInfoModal] = useState(false);
  const [openProcessDirectionModal, setOpenProcessDirectionModal] = useState(false);
  const [openAskDoctorModal, setOpenAskDoctorModal] = useState(false);
  const [openPregnantControlModal, setOpenPregnantControlModal] = useState(false);
  const [openHivModal, setOpenHivModal] = useState(false);
  const [openPacemakerModal, setOpenPacemakerModal] = useState(false);
  const [openConsultationModal, setOpenConsultationModal] = useState(false);
  const [openLabRequestModal, setOpenLabRequestModal] = useState(false);
  const [openPathologyModal, setOpenPathologyModal] = useState(false);
  const [openPatientFileChangeModal, setOpenPatientFileChangeModal] = useState(false);
  const [openPatientProcess, setOpenPatientProcess] = useState(false);
  const [openTreatmentApplicationsModalState, setOpenTreatmentApplicationsModalState] = useState(false);
  const [treatmentApplicationsPatient, setTreatmentApplicationsPatient] = useState<any | null>(null);
  const handleOpen = () => setOpenMenu(true);
  const handleClose = () => setOpenMenu(false);
  const handleOpenMC = () => setOpenMenuMC(true);
  const handleCloseMC = () => setOpenMenuMC(false);
  const handleOpenMedCard = () => setOpenMedCard(true);
  const handleCloseMedCard = () => setOpenMedCard(false);
  const handleOpenRequestService = () => setOpenRequestService(true);
  const handleCloseRequestService = () => setOpenRequestService(false);
  const handleOpenAddService = () => setOpenAddService(true);
  const handleCloseAddService = () => setOpenAddService(false);
  const [menus, setmenus] = useState<any>([]);
  const [title, setTitle] = useState("");
  const [rowID, setRowID] = useState("");
  const [description, setDescription] = useState("");
  const [upn, setUpn] = useState<string>("");

  const getDetailPanelContent = ({ row }: any) => {
    return <DetailPanel data={row} />;
  };

  const getDetailPanelHeight = () => "auto";

  const getRowHeight = useCallback(
    (params: any) => {
      if (!params?.row) return undefined;
      const isoCount = Array.isArray(params.row?.isolationtypeids)
        ? params.row.isolationtypeids.length
        : 0;
      // 3+ ikon varsa satırı biraz yükselt
      if (isoCount > 3) return 72;
      return undefined; // varsayılan
    },
    []
  );

  const handleOpenColumnSelector = () => {
    // Grid toolbar içindeki "Columns" (veya lokalize karşılığı) butonunu programatik olarak tetikle
    const columnsButton = (document.querySelector(
      'button[aria-label*="Columns"], button[aria-label*="columns"], button[aria-label*="Sütun"], button[aria-label*="sütun"]'
    ) ||
      document.querySelector(
        'button[title*="Columns"], button[title*="columns"], button[title*="Sütun"], button[title*="sütun"]'
      )) as HTMLButtonElement | null;

    columnsButton?.click();
  };

  const handleRowClick = (params: any) => {
    // Ekle kolonuna tıklanınca sadece menü açılsın, detay paneli tetiklenmesin
    if (params.field === "Ekle") return;

    setExpandedRowIds((prev) =>
      prev.includes(params.id)
        ? prev.filter((id) => id !== params.id)
        : [...prev, params.id]
    );
  };

  const [columnVisibilityModel, setColumnVisibilityModel] = useState<any>(() => {
    const saved = localStorage.getItem("nurse-patient-list-column-visibility");
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch (e) {
        console.error("Failed to parse saved column visibility:", e);
      }
    }
    return {
      __detail_panel_toggle__: false,
      age: true,
      diseasecodes: false,
      deptname: false,
      assname: false,
      secondaryassname: false,
      floorinfo: false,
      patienttype: false,
      patientlanguage: false,
      patientgroup: false,
      hospitalizationduration: false,
      dischargetime: false,
      bloodgroup: false,
      hasfallen: false,
      filestatus: false,
      nurse: false,
      consultation: false,
      psychologist: false,
      treatmenttype: false,
      iscalledfortest: false,
      hospitalizationtime: false,
      observationstart: false,
      observationend: false,
      ispregnant: false,
      hasration: false,
      hascompanion: false,
      hasprosthesis: false,
      hasallergy: false,
      hasorder: false,
      hasairway: false,
      hasradiology: false,
      hasbloodrequest: false,
      hasepikriz: false,
      hasmonitoring: false,
      haslab: false,
      haspain: false,
      hasfallrisk: false,
      haspregnant: false,
    };
  });

  const handleColumnVisibilityModelChange = (newModel: any) => {
    setColumnVisibilityModel(newModel);
    localStorage.setItem("nurse-patient-list-column-visibility", JSON.stringify(newModel));
  };

  const handleCardAction = (action: string, row: any) => {
    switch (action) {
      case "add": // "+" button -> İşlemler Popup
        openModal(row);
        break;
      case "ai-analysis":
        setAiAnalysisState({
          open: true,
          patient: row,
          status: "loading",
          report: null,
          stepFlushPending: false,
        });
        break;
      case "opened-medcards": // "A. Kartlar"
        openModalMedcard(row);
        break;
      case "patient-table":
        navigate(`/nurse/patient-table/${row.id}`);
        break;
      case "treatments":
        openTreatmentApplicationsModal(row);
        break;
      case "patient-processes": // ""
        openProcessesModal(row);
        break;
      case "vital-signs":
        navigate(`/medcard/vital-signs/${row.id}`);
        break;
      case "note-view":
        setSpeakToTextState({ open: true, row, mode: "followup" });
        break;
      case "note-nurse":
        setSpeakToTextState({ open: true, row, mode: "note" });
        break;
      default:
        openModal(row);
        break;
    }
  };

  useEffect(() => {
    if (!aiAnalysisState.open || !aiAnalysisState.patient) {
      return;
    }

    let isCancelled = false;
    const abortController = new AbortController();
    const patientRow = aiAnalysisState.patient;
    const ptId = Number(patientRow?.ptid ?? patientRow?.id ?? 0);
    const requestBody = buildNurseListPatientAnalyzeRequest(ptId);

    const finishSuccess = (resolvedPtId: number, text: string) => {
      if (isCancelled) return;
      const trimmed = text.trim();
      setAiAnalysisState((prev) => {
        if (!prev.open) return prev;
        return {
          ...prev,
          status: "success",
          stepFlushPending: false,
          report: {
            ptId: resolvedPtId,
            text: trimmed || "Analiz metni uretilemedi.",
          },
        };
      });
    };

    const runPatientAnalyze = async () => {
      try {
        const streamedText = await patientAnalyzeStream(requestBody, {
          signal: abortController.signal,
          onDelta: (accumulated) => {
            if (isCancelled) return;
            setAiAnalysisState((prev) => {
              if (!prev.open) return prev;
              const nextReport = { ptId, text: accumulated };
              const hasText = accumulated.trim().length > 0;
              if (prev.status === "loading" && hasText && !prev.stepFlushPending) {
                return {
                  ...prev,
                  report: nextReport,
                  stepFlushPending: true,
                };
              }
              if (prev.stepFlushPending) {
                return { ...prev, report: nextReport };
              }
              return {
                ...prev,
                status: "streaming",
                report: nextReport,
              };
            });
          },
        });

        if (isCancelled) return;

        finishSuccess(ptId, streamedText || "Analiz metni uretilemedi.");
      } catch (streamError: any) {
        if (isCancelled || streamError?.name === "AbortError") return;

        const msg =
          typeof streamError?.message === "string" && streamError.message.trim()
            ? streamError.message.trim()
            : "Analiz akışı tamamlanamadı. Lütfen daha sonra tekrar deneyin.";
        finishSuccess(ptId, msg);
      }
    };

    runPatientAnalyze();

    return () => {
      isCancelled = true;
      abortController.abort();
    };
  }, [aiAnalysisState.open, aiAnalysisState.patient]);


  const cardViewData = useMemo(() => {
    const rawData = (nurseData as any)?.data || [];
    if (!Array.isArray(rawData)) return [];

    let filteredData = rawData;

    if (searchTerm) {
      const lowerQuery = searchTerm.toLowerCase();
      filteredData = rawData.filter((item: any) => {
        const name = (item.name || "").toLowerCase();
        const surname = (item.surname || "").toLowerCase();
        const upn = (item.upn || "").toString();
        const protid = (item.protid || "").toString();
        const combined = `${name} ${surname}`;

        return (
          name.includes(lowerQuery) ||
          surname.includes(lowerQuery) ||
          combined.includes(lowerQuery) ||
          upn.includes(lowerQuery) ||
          protid.includes(lowerQuery)
        );
      });
    }

    // Create a shallow copy to sort
    const sortedData = [...filteredData];

    if (selectedTab === 0) {
      // Alphabetical by name
      sortedData.sort((a, b) => {
        const nameA = a.name || "";
        const nameB = b.name || "";
        return nameA.localeCompare(nameB, "tr");
      });
    } else if (selectedTab === 1) {
      // Discharge time descending
      sortedData.sort((a, b) => {
        const dateA = a.dischargetime;
        const dateB = b.dischargetime;

        if (!dateA) return 1;
        if (!dateB) return -1;

        try {
          // Check if ISO
          if (dateA.includes("T")) {
            return new Date(dateB).getTime() - new Date(dateA).getTime();
          }

          // "dd.MM.yyyy HH:mm"
          const parseTrDate = (dStr: string) => {
            const [d, t] = dStr.split(" ");
            if (!d) return new Date();
            const [day, month, year] = d.split(".").map(Number);
            const [hour, minute] = t ? t.split(":").map(Number) : [0, 0];
            return new Date(year, month - 1, day, hour, minute);
          }

          const dA = parseTrDate(dateA);
          const dB = parseTrDate(dateB);
          return dB.getTime() - dA.getTime();
        } catch (e) {
          return 0;
        }
      });
    }
    // Tab 2 (Observation) - No specific sort mentioned, default ok

    return sortedData;
  }, [nurseData, selectedTab, searchTerm]);

  useEffect(() => {
    const rawData = (nurseData as any)?.data;
    if (!Array.isArray(rawData)) {
      setVisitTypeOverrides({});
      return;
    }

    setVisitTypeOverrides((prev) => {
      const next: Record<number, { id: number; name: string }> = {};

      rawData.forEach((item: any) => {
        const patientId = Number(item?.ptid ?? item?.id);
        if (!patientId || !prev[patientId]) {
          return;
        }

        const backendVisitTypeId = item?.visittypeid;
        const backendVisitTypeName = item?.visittypename;

        if (
          Number(backendVisitTypeId) === Number(prev[patientId].id) &&
          String(backendVisitTypeName ?? "").trim() === String(prev[patientId].name ?? "").trim()
        ) {
          return;
        }

        next[patientId] = prev[patientId];
      });

      const prevKeys = Object.keys(prev);
      const nextKeys = Object.keys(next);
      if (prevKeys.length === nextKeys.length && prevKeys.every((key) => next[Number(key)])) {
        return prev;
      }

      return next;
    });
  }, [nurseData]);

  return (
    <>
      <Stack
        m={{ xs: 0.5, md: 1 }}
        direction={{ xs: "column", md: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "stretch", md: "center" }}
        spacing={{ xs: 1.5, md: 2 }}
      >
        <Stack
          sx={{
            width: { xs: "100%", md: "360px" },
            padding: "17px 12px",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            gap: "10px",
            flexShrink: 0,
            alignSelf: "stretch",
            borderRadius: "20px",
            background: "#FFF",
          }}
        >
          <Tabs
            sx={{
              minHeight: 0,
              "& .MuiTabs-indicator": {
                backgroundColor: "rgba(33, 150, 243, 1)",
                height: "2px",
              },
            }}
            value={selectedTab}
            onChange={(event, newValue) => setSelectedTab(newValue)}
            aria-label="Tabs"
          >
            <Tab
              label={t("inpatient")}
              value={0}
              sx={{
                minHeight: 0,
                textTransform: "none",
                fontSize: "14px",
                fontWeight: 600,
                color: "rgba(0, 0, 0, 0.38)",
                pb: "8px",
                "&.Mui-selected": {
                  color: "rgba(33, 150, 243, 1)",
                },
              }}
            />
            <Tab
              label={t("observation")}
              value={2}
              sx={{
                minHeight: 0,
                textTransform: "none",
                fontSize: "14px",
                fontWeight: 600,
                color: "rgba(0, 0, 0, 0.38)",
                pb: "8px",
                "&.Mui-selected": {
                  color: "rgba(33, 150, 243, 1)",
                },
              }}
            />
            <Tab
              label={t("outpatient")}
              value={1}
              sx={{
                minHeight: 0,
                textTransform: "none",
                fontSize: "14px",
                fontWeight: 600,
                color: "rgba(0, 0, 0, 0.38)",
                pb: "8px",
                "&.Mui-selected": {
                  color: "rgba(33, 150, 243, 1)",
                },
              }}
            />
          </Tabs>
        </Stack>

        <Stack
          direction="row"
          spacing={2}
          useFlexGap
          sx={{
            width: "100%",
            minHeight: 64,
            borderRadius: "20px",
            backgroundColor: "#ffffff",
            padding: "12px 16px",
            boxSizing: "border-box",
            alignItems: "center",
            flexWrap: "wrap",
            justifyContent: "flex-start",
          }}
        >
          {selectedTab !== 0 && (
            <>
              <Box sx={{ flex: "1 1 0", minWidth: { xs: "100%", sm: 180 } }}>
                <ByzMuiDatePicker
                  label={t("start-date")}
                  value={startDate ? parseISO(startDate) : null}
                  onChange={(date: any) => {
                    setStartDate(toLocalDate(date));
                  }}
                  format="dd.MM.yyyy"
                  slotProps={{ textField: { placeholder: "GG.AA.YYYY" } }}
                  fullWidth
                />
              </Box>
              <Box sx={{ flex: "1 1 0", minWidth: { xs: "100%", sm: 180 } }}>
                <ByzMuiDatePicker
                  label={t("end-date")}
                  value={endDate ? parseISO(endDate) : null}
                  onChange={(date: any) => {
                    setEndDate(toLocalDate(date));
                  }}
                  format="dd.MM.yyyy"
                  slotProps={{ textField: { placeholder: "GG.AA.YYYY" } }}
                  fullWidth
                />
              </Box>
            </>
          )}
          <ByzOfficesList
            multiple={false}
            disableClearable={true}
            value={
              user?.UserAssPriviliges?.find((o) => o.ID === selectedOfficeId)
                ? {
                  value: String(selectedOfficeId),
                  label: user.UserAssPriviliges.find((o) => o.ID === selectedOfficeId)?.OFFICE_NAME || "",
                }
                : null
            }
            handleChange={(val: any) => {
              if (val) {
                const nextOfficeId = Number(val.value);
                setSelectedOfficeId(nextOfficeId);
                dispatch(setSelectedOfficeIdGlobal(nextOfficeId));
              }
            }}
            sx={{ flex: "1 1 0", minWidth: { xs: "100%", sm: 180 } }}
          />
          <FloorList
            offId={effectiveOffId}
            handleChange={(val: any) => {
              if (val) {
                setSelectedFloorId(val.id);
              } else {
                setSelectedFloorId(0);
              }
            }}
            sx={{ flex: "1 1 0", minWidth: { xs: "100%", sm: 180 } }}
          />
          <DepartmentList
            offId={effectiveOffId}
            handleChange={(val: any) => {
              if (val) {
                setSelectedDepartmentId(val.id);
              } else {
                setSelectedDepartmentId(0);
              }
            }}
            sx={{ flex: "1 1 0", minWidth: { xs: "100%", sm: 180 } }}
          />
          <ByzNurseList
            offId={effectiveOffId}
            deptId={selectedDepartmentId}
            handleChange={(val: any) => {
              if (val && !Array.isArray(val)) {
                setSelectedNurseId(val.id);
              } else {
                setSelectedNurseId(0);
              }
            }}
            sx={{ flex: "1 1 0", minWidth: { xs: "100%", sm: 180 } }}
          />

          <ByzMuiSearchButton
            onClick={() => getNursePatientList()}
            loading={nurserMutation.isPending}
          />

          <Divider orientation="vertical" flexItem sx={{ height: 24, my: 'auto', mx: 1, borderColor: '#E5E8EB' }} />

          <ByzViewModeSelector
            viewMode={viewMode}
            onChange={setViewMode}
            sx={{ flex: "0 0 auto" }}
          />

          {viewMode === 'card' && (
            <FormControlLabel
              control={
                <Checkbox
                  checked={syncScroll}
                  onChange={(e) => setSyncScroll(e.target.checked)}
                  icon={<Iconify icon="mdi:checkbox-blank-outline" width={22} />}
                  checkedIcon={<Iconify icon="mdi:checkbox-marked" width={22} />}
                  sx={{
                    p: 0.5,
                    color: 'rgba(0, 0, 0, 0.38)',
                    '&.Mui-checked': {
                      color: '#2196f3',
                    },
                  }}
                />
              }
              label={t("nurse.patient-list.sync-scroll") || "Kartları Senkron Kaydır"}
              sx={{
                ml: 1,
                mr: 1,
                '& .MuiTypography-root': {
                  fontSize: '13px',
                  fontWeight: 600,
                  color: '#1b1b1b',
                  fontFamily: 'Poppins',
                  lineHeight: '150%'
                }
              }}
            />
          )}
        </Stack>
      </Stack>

      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={1}
        sx={{
          mx: { xs: 0.5, md: 1 },
          mt: { xs: 0.5, md: 1 },
          mb: { xs: 1, md: 1.5 },
          alignItems: "flex-start",
        }}
      >

      </Stack>

      <Stack
        sx={{
          mt: { xs: 1, md: 2 },
          backgroundColor: viewMode === 'card' ? 'transparent' : "#ffffff", // Transparent for card view grid
          borderRadius: "20px",
          boxShadow: viewMode === 'card' ? 'none' : "0px 10px 30px rgba(15, 23, 42, 0.06)", // Remove shadow for grid container
          minHeight: 400
        }}
      >
        {viewMode === 'list' ? (
          <ByzMuiDataGrid
            columns={columns}
            rows={(nurseData as any)?.data || []}
            loading={nurserMutation.isPending}
            getRowId={(row) => row.id}
            customToolbar={ByzDataGridToolbar}
            getDetailPanelContent={getDetailPanelContent}
            getDetailPanelHeight={getDetailPanelHeight}
            // Satıra tıklayınca detay panelini aç/kapa
            onRowClick={handleRowClick}
            getRowHeight={getRowHeight}
            columnVisibilityModel={columnVisibilityModel}
            onColumnVisibilityModelChange={handleColumnVisibilityModelChange}
            headerWhite
            sx={{
              flexGrow: 1,
              "& .MuiDataGrid-cell[data-field='operationstatus']": {
                textOverflow: "clip",
              },
            }}
            // Toggle kolonundaki (+) ikonunu gizle
            initialState={{
              pagination: { paginationModel: { pageSize: 15 } },
              columns: {
                columnVisibilityModel: {
                  __detail_panel_toggle__: false,
                  age: true,
                  diseasecodes: false,
                  deptname: false,
                  assname: false,
                  secondaryassname: false,
                  floorinfo: false,
                  patienttype: false,
                  patientlanguage: false,
                  patientgroup: false,
                  hospitalizationduration: false,
                  dischargetime: false,
                  bloodgroup: false,
                  hasfallen: false,
                  filestatus: false,
                  nurse: false,
                  consultation: false,
                  psychologist: false,
                  treatmenttype: false,
                  iscalledfortest: false,
                  hospitalizationtime: false,
                  observationstart: false,
                  observationend: false,
                  ispregnant: false,
                  hasration: false,
                  hascompanion: false,
                  hasprosthesis: false,
                  hasallergy: false,
                  hasorder: false,
                  hasairway: false,
                  hasradiology: false,
                  hasbloodrequest: false,
                  hasepikriz: false,
                  hasmonitoring: false,
                  haslab: false,
                  haspain: false,
                  hasfallrisk: false,
                  haspregnant: false,
                },
              },
            }}
            detailPanelExpandedRowIds={expandedRowIds}
            onDetailPanelExpandedRowIdsChange={(newIds: GridRowId[]) =>
              setExpandedRowIds(newIds)
            }
          />
        ) : (
          <Stack spacing={2} sx={{ width: '100%', height: '100%' }}>
            {/* Local Search for Card View */}
            <Box sx={{ width: '100%', px: 0.5 }}>
              <ByzMuiTextField
                label={t("search")}
                placeholder={t("common.search-in-list")}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                fullWidth
                sx={{ maxWidth: 400, backgroundColor: '#fff' }}
                InputProps={{
                  startAdornment: (
                    <Iconify icon="eva:search-fill" sx={{ color: 'text.disabled', mr: 1 }} />
                  ),
                }}
              />
            </Box>
            <PatientCardView
              data={cardViewData}
              loading={nurserMutation.isPending}
              onAction={handleCardAction}
              t={t}
              isolationIconsMap={isolationIconsMap}
              visitTypes={visitTypes}
              visitTypeOverrides={visitTypeOverrides}
              onVisitTypeSaved={handleVisitTypeSaved}
              onVisitTypeUpdating={setVisitTypeUpdating}
              syncScroll={syncScroll}
            />
          </Stack>
        )}
      </Stack>

      <AddMedicalCardModal
        open={openMenuMC}
        onClose={() => {
          handleCloseMC();
          handleClose();
        }}
        title={title}
        description={description}
        rowID={rowID}
        roomNumber={roomNumber}
        age={age}
        gender={gender}
      />

      <OpenedMedcards
        open={openMedCards}
        onClose={() => {
          handleCloseMedCard();
          handleClose();
        }}
        ptId={rowID}
        upn={upn}
        gender={gender}
      />

      <ByzMuiSpeakToTextModal
        open={speakToTextState.open}
        onClose={() =>
          setSpeakToTextState({ open: false, row: null, mode: "followup" })
        }
        onSaved={() => {
          // Not/izlem eklendikten sonra kart ikonlarını güncelle
          getNursePatientListStable();
        }}
        patientId={speakToTextState.row?.id}
        patientName={
          speakToTextState.row
            ? `${speakToTextState.row.name ?? ""} ${speakToTextState.row.surname ?? ""}`.trim()
            : ""
        }
        clinicName={speakToTextState.row?.deptname}
        roomInfo={speakToTextState.row?.bedinfo}
        gender={speakToTextState.row?.sexuality}
        mode={speakToTextState.mode}
      />

      <PatientAiAnalysisDrawer
        open={aiAnalysisState.open}
        patient={aiAnalysisState.patient}
        status={aiAnalysisState.status}
        report={aiAnalysisState.report}
        stepFlushPending={aiAnalysisState.stepFlushPending}
        onStepFlushComplete={() =>
          setAiAnalysisState((prev) => {
            if (prev.status !== "loading" || !prev.stepFlushPending) return prev;
            return { ...prev, stepFlushPending: false, status: "streaming" };
          })
        }
        onClose={() =>
          setAiAnalysisState({
            open: false,
            patient: null,
            status: "idle",
            report: null,
            stepFlushPending: false,
          })
        }
      />

      <MenuModal
        open={openMenu}
        onClose={handleClose}
        title={""}
        description={""}
        menus={menus}
        departmentName={departmentName}
        roomNumber={roomNumber}
        patientFullName={title}
        age={age}
        gender={gender}
      />

      <Backdrop
        open={visitTypeUpdating}
        sx={{
          color: "#fff",
          zIndex: (theme) => theme.zIndex.modal + 2,
          flexDirection: "column",
          gap: 2,
        }}
      >
        <CircularProgress color="inherit" />
        <Typography sx={{ fontWeight: 600 }}>{t("loading")}</Typography>
      </Backdrop>

      {/* 
        Custom Modal Düzenlenecek.BKARADENIZ
      */}

      <CustomModal
        open={open}
        onClose={handleClose}
        title={title}
        body={description}
      />

      <RequestServiceModal
        open={openRequestService}
        onClose={handleCloseRequestService}
        title={title}
        description={description}
        rowID={rowID}
        upn={upn}
      />

      <AddServiceModal
        open={openAddService}
        onClose={handleCloseAddService}
        title={title}
        description={description}
        rowID={rowID}
        upn={upn}
      />

      <AddStockModal
        open={openAddStock}
        onClose={() => setOpenAddStock(false)}
        title={title}
        description={description}
        upn={upn}
      />

      <PatientProcess
        open={openPatientProcess}
        onClose={() => setOpenPatientProcess(false)}
        ptId={rowID}
        upn={upn}
      />

      <TreatmentApplicationsModal
        open={openTreatmentApplicationsModalState}
        onClose={() => {
          setOpenTreatmentApplicationsModalState(false);
          setTreatmentApplicationsPatient(null);
        }}
        patientId={Number(treatmentApplicationsPatient?.id ?? 0)}
        upn={treatmentApplicationsPatient?.upn}
        patientName={
          treatmentApplicationsPatient
            ? `${treatmentApplicationsPatient.name ?? ""} ${treatmentApplicationsPatient.surname ?? ""}`.trim()
            : ""
        }
        gender={
          treatmentApplicationsPatient?.gender ||
          treatmentApplicationsPatient?.sex ||
          treatmentApplicationsPatient?.sexuality
        }
        isPregnant={Boolean(
          treatmentApplicationsPatient?.ispregnant ?? treatmentApplicationsPatient?.isPregnant
        )}
      />

      <HeightInfoModal
        open={openHeightInfoModal}
        onClose={() => setOpenHeightInfoModal(false)}
      />

      <HIVModal
        open={openHivModal}
        onClose={() => setOpenHivModal(false)}
        serviceName={title || "Seçilen Hizmetin Adı"}
      />

      <PacemakerModal
        open={openPacemakerModal}
        onClose={() => setOpenPacemakerModal(false)}
      />

      <ConsultationModal
        open={openConsultationModal}
        onClose={() => setOpenConsultationModal(false)}
      />

      <LabRequestModal
        open={openLabRequestModal}
        onClose={() => setOpenLabRequestModal(false)}
      />

      <PathologyModal
        open={openPathologyModal}
        onClose={() => setOpenPathologyModal(false)}
      />

      <AskDoctorModal
        open={openAskDoctorModal}
        onClose={() => setOpenAskDoctorModal(false)}
        serviceName={title}
      />

      <PregnantControlModal
        open={openPregnantControlModal}
        onClose={() => setOpenPregnantControlModal(false)}
      />

      <ProcessDirectionModal
        open={openProcessDirectionModal}
        onClose={() => setOpenProcessDirectionModal(false)}
        serviceName={title}
      />

      <PatientComingFileChangeModal
        open={openPatientFileChangeModal}
        onClose={() => setOpenPatientFileChangeModal(false)}
      />
    </>
  );
}

export default List;


